// import { useTranslation } from 'react-i18next'
import { useRef, useState, useEffect } from 'react'

import zxcvbn from 'zxcvbn'
import TextField from '@/components/forms/fields/text-field'
import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'

// Export the scoring function so it can be used by other components
export const getScoreValuesByCrackTime = (crackTimeStr: string, pw?: string, t?: any) => {
  // Default translation function if not provided
  const translate = t || ((key: string) => key)

  // Extract the time unit from crack time string
  let timeUnit = crackTimeStr.toLowerCase()

  // Convert days to weeks if 8 or more days (including 8-13 range)
  const daysMatch = /^(\d+) days?$/.exec(timeUnit)

  const veryWeak = translate('very weak')
  const weak = translate('weak')
  const good = translate('good')
  const strong = translate('strong')
  const secondsToMinutes = translate('seconds to minutes')
  const hoursToWeeks = translate('hours to weeks')
  const monthsToYears = translate('months to years')
  const centuries = translate('centuries')

  if (daysMatch) {
    const days = parseInt(daysMatch[1])
    if (days >= 14) {
      const weeks = Math.floor(days / 7)
      timeUnit = `${weeks} weeks`
    } else if (days >= 8) {
      // Convert 8-13 days to "1 week"
      timeUnit = '1 week'
    }
  }

  // Very weak: seconds
  if (timeUnit.includes('second')) {
    return {
      score: veryWeak,
      scoreColor: 'text-accent-melon!',
      barColor: 'bg-accent-melon',
      fillPercentage: 10,
      level: secondsToMinutes,
    }
  }

  // Very weak: minutes
  if (timeUnit.includes('minute')) {
    return {
      score: veryWeak,
      scoreColor: 'text-accent-melon!',
      barColor: 'bg-accent-melon',
      fillPercentage: 23,
      level: secondsToMinutes,
    }
  }

  // Weak: hour/s
  if (timeUnit.includes('hour')) {
    return {
      score: weak,
      scoreColor: 'text-accent-gold!',
      barColor: 'bg-accent-gold',
      fillPercentage: 36,
      level: hoursToWeeks,
    }
  }

  // Weak: day/s
  if (timeUnit.includes('day') || timeUnit.includes('week')) {
    return {
      score: weak,
      scoreColor: 'text-accent-gold!',
      barColor: 'bg-accent-gold',
      fillPercentage: 49,
      level: hoursToWeeks,
    }
  }

  // Good: week or weeks
  if (timeUnit.includes('month')) {
    return {
      score: good,
      scoreColor: 'text-green-500! dark:text-green-500!',
      barColor: 'bg-green-500',
      fillPercentage: 62,
      level: monthsToYears,
    }
  }

  // Good: years
  if (timeUnit.includes('year')) {
    return {
      score: good,
      scoreColor: 'text-green-500! dark:text-green-500!',
      barColor: 'bg-green-500',
      fillPercentage: 75,
      level: monthsToYears,
    }
  }

  // Strong: century or centuries (shorter passwords)
  if (timeUnit.includes('centur') && pw && pw?.length < 16) {
    return {
      score: strong,
      scoreColor: 'text-primaryBlue!',
      barColor: 'bg-primaryBlue',
      fillPercentage: 88,
      level: centuries,
    }
  }

  // Strong: century or centuries (longer passwords)
  if (timeUnit.includes('centur') && pw && pw?.length >= 16) {
    return {
      score: strong,
      scoreColor: 'text-primaryBlue!',
      barColor: 'bg-primaryBlue',
      fillPercentage: 100,
      level: centuries,
    }
  }

  // Default fallback
  return {
    score: veryWeak,
    scoreColor: 'text-accent-melon!',
    barColor: 'bg-accent-melon',
    fillPercentage: 25,
    level: translate('seconds to minutes'),
  }
}

function SectionPasswordTest() {
  useEffect(() => {
    const checkUrlAndFocus = () => {
      const url = window.location.href

      // If the URL hash is #Password-Strength-Testing-Tool, focus the password input field
      if (url.includes('#Password-Strength-Testing-Tool')) {
        const input = document.getElementById('password-input')
        input?.focus()
      }
    }

    // Check on mount and when page changes
    checkUrlAndFocus()

    // Listen for custom hash navigation events from LinkComponent
    const handleHashNavigation = () => {
      checkUrlAndFocus()
    }

    window.addEventListener('hashnavigation', handleHashNavigation as EventListener)

    return () => {
      window.removeEventListener('hashnavigation', handleHashNavigation as EventListener)
    }
  }, [])

  const { t } = useTranslations()
  const [pass, setPass] = useState('')
  const renderCount = useRef(0)
  renderCount.current += 1

  const zxcvbnObj = zxcvbn(pass)
  const crackTime = zxcvbnObj.crack_times_display.offline_slow_hashing_1e4_per_second

  const { score, scoreColor, barColor, fillPercentage } = getScoreValuesByCrackTime(String(crackTime), pass, t)

  const lang: Record<string, string> = {
    'less than a second': t('less than a second'),
    second: t('second'),
    seconds: t('seconds'),
    minute: t('minute'),
    minutes: t('minutes'),
    hour: t('hour'),
    hours: t('hours'),
    day: t('day'),
    days: t('days'),
    week: t('week'),
    weeks: t('weeks'),
    month: t('month'),
    months: t('months'),
    year: t('year'),
    years: t('years'),
    century: t('century'),
    centuries: t('centuries'),
  }
  // Convert crack time to string if it's a number
  const crackTimeStr = typeof crackTime === 'string' ? crackTime : String(crackTime)
  const matches = crackTimeStr.match(/^(\d+) (\w+)$/)
  const crackTimeLang = matches && matches.length === 3 ? `${Number(matches[1])} ${lang[matches[2]]}` : lang[crackTime]

  return (
    <div className="lg:max-w-8xl m-auto px-5 lg:px-9" id="Password-Strength-Testing-Tool">
      <div className="bg-white dark:bg-secondary-800 px-5 lg:px-32 py-10 lg:py-16  rounded-lg border-b-2 border-secondary-100">
        <div className="text-center pb-2 lg:pb-6">
          <h1>{t('Password Strength Testing Tool')}</h1>
          <p>{t('Think you have a strong password? Find out below.')}</p>
        </div>

        <div className="max-w-5xl mx-auto pt-0 md:px-12">
          <TextField
            id="password-input"
            className={cx('font-mono text-xl my-2 sm:text-4xl sm:my-5 truncate', scoreColor)}
            type="text"
            name="password"
            label={t('Evaluate your password:')}
            value={pass}
            onUpdate={(val) => setPass(val)}
            autoFocus
            spellCheck={false}
          />
          <div>
            <div className="flex py-5 font-semibold">
              <div className="flex-1">{t('Your password strength')}</div>
              <div className="flex-none">
                <div className="relative">
                  <span
                    className={cx(pass && barColor, 'rounded-4xl py-2 px-4 text-white transition-colors duration-100')}
                  >
                    {pass && <span className="capitalize px-8">{t(`${score}`)}</span>}
                  </span>
                </div>
              </div>
            </div>
            <div className="w-full h-3 bg-secondary-100 dark:bg-secondary-700 rounded-full overflow-hidden mt-2 mb-4">
              <div
                className={cx('h-full rounded-full transition-all duration-500 ease-out', pass && barColor)}
                style={{
                  width: `${!pass ? 0 : fillPercentage}%`,
                  transition: 'width 0.5s ease-out, background-color 0.3s ease-out',
                }}
              />
            </div>
          </div>
          <div>
            {t('Estimated time to crack')} :{' '}
            {pass ? !!renderCount.current && <span className="capitalize font-[600]">{crackTimeLang}</span> : '0'}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SectionPasswordTest
